import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Bell, Check, Inbox, Crown, Megaphone, Trophy, Calendar, AlertCircle, Users,
} from "lucide-react";

/* Helencia notification bell — Navbar dropdown with unread badge */

const ICONS = {
  new_application: Inbox,
  application_accepted: Check,
  application_refused: AlertCircle,
  profile_incomplete: AlertCircle,
  admin_broadcast: Megaphone,
  concours: Trophy,
  event: Calendar,
  premium: Crown,
};

function pickIcon(type = "") {
  if (type.startsWith("collab_expiring")) return AlertCircle;
  if (type.startsWith("collab_auto_closed")) return AlertCircle;
  if (ICONS[type]) return ICONS[type];
  return Bell;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function NotificationBell({
  dark = false,       // dark mode (for admin sidebar)
  listHref,           // where the "Tout voir" link goes (role-dependent)
  testId = "notification-bell",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef();
  const navigate = useNavigate();

  // poll unread count
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const fetchCount = () =>
      api.get("/notifications/unread-count")
        .then(({ data }) => mounted && setCount(data.count || 0))
        .catch(() => {});
    fetchCount();
    const iv = setInterval(fetchCount, 45000);
    return () => { mounted = false; clearInterval(iv); };
  }, [user]);

  // fetch list when opening
  useEffect(() => {
    if (!open) return;
    api.get("/notifications").then(({ data }) => setItems(data.slice(0, 8))).catch(() => {});
  }, [open]);

  // click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openAndHandle = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
      setCount((c) => Math.max(0, c - 1));
      setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    if (n.link) {
      setOpen(false);
      if (n.link.startsWith("http")) window.location.href = n.link;
      else navigate(n.link);
    }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch (e) { /* ignore */ }
    setCount(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  if (!user) return null;

  const bellBg = dark
    ? "bg-white/10 hover:bg-white/20"
    : "bg-white border border-[#EADDC9] hover:border-[#D4A574] shadow-sm";
  const iconColor = dark ? "text-white" : "text-[#1A1A1A]";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative w-10 h-10 rounded-full ${bellBg} flex items-center justify-center transition`}
        data-testid={testId}
        aria-label="Notifications"
      >
        <Bell className={`w-4 h-4 ${iconColor}`} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C68E59] text-white text-[10px] font-bold border-2 border-white flex items-center justify-center"
            data-testid="notification-badge-count"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-3 w-[380px] max-w-[92vw] rounded-3xl bg-white border border-[#EADDC9] shadow-[0_20px_60px_-20px_rgba(110,75,42,0.25)] z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#FAF3EA]">
            <div>
              <p className="caption-label text-[#A26F3E]">Notifications</p>
              <p className="font-serif text-xl text-[#1A1A1A] leading-tight mt-0.5">
                {count > 0 ? `${count} non lue${count > 1 ? "s" : ""}` : "Tout est à jour"}
              </p>
            </div>
            {count > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-[#A26F3E] hover:text-[#6E4B2A] font-medium"
                data-testid="notification-mark-all-read"
              >
                Tout marquer lu
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-7 h-7 text-[#D4C3A5] mx-auto mb-2" />
                <p className="text-sm text-[#8C7D6B]">Aucune notification pour le moment.</p>
              </div>
            ) : (
              <ul>
                {items.map((n) => {
                  const Icon = pickIcon(n.type);
                  return (
                    <li key={n.id}>
                      <button
                        onClick={() => openAndHandle(n)}
                        className={`w-full text-left flex gap-3 px-5 py-3.5 border-b border-[#FAF3EA] last:border-0 hover:bg-[#FFFBF5] transition ${!n.read ? "bg-[#FDF7EE]" : ""}`}
                        data-testid={`notif-item-${n.id}`}
                      >
                        <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${!n.read ? "bg-[#C68E59] text-white" : "bg-[#FAF3EA] text-[#A26F3E]"}`}>
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-[#1A1A1A] leading-tight truncate">
                            {n.title}
                          </span>
                          <span className="block text-xs text-[#6E4B2A] mt-0.5 line-clamp-2">
                            {n.body}
                          </span>
                          <span className="block text-[10px] text-[#8C7D6B] tracking-wide uppercase mt-1">
                            {timeAgo(n.created_at)}
                          </span>
                        </span>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#C68E59] mt-2 shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {listHref && (
            <Link
              to={listHref}
              onClick={() => setOpen(false)}
              className="block text-center py-3 text-xs tracking-widest uppercase text-[#A26F3E] hover:text-[#6E4B2A] border-t border-[#FAF3EA] bg-[#FFFBF5]"
              data-testid="notification-view-all"
            >
              Voir toutes les notifications
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
